import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Save } from 'lucide-react';
import { useActiveOrganizationId } from '@repo/api-client/react';
import {
  useAppDefaults,
  useOrganization,
  useSupportedCurrencies,
  useUpdateAppDefaults,
  useUpdateOrganization,
} from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { useActiveRole } from '@/features/auth/useActiveRole';
import { cn } from '@/lib/utils';

const PAY_TERMS = [
  { id: 'net7', label: 'Net 7' },
  { id: 'net14', label: 'Net 14' },
  { id: 'net30', label: 'Net 30' },
  { id: 'eom', label: 'End of month' },
  { id: 'due', label: 'Due on receipt' },
] as const;

const FALLBACK_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR'];

function readBrandingString(
  branding: Record<string, unknown> | null | undefined,
  key: string,
): string {
  const value = branding?.[key];
  return typeof value === 'string' ? value : '';
}

function readBrandingBoolean(
  branding: Record<string, unknown> | null | undefined,
  key: string,
  fallback: boolean,
): boolean {
  const value = branding?.[key];
  return typeof value === 'boolean' ? value : fallback;
}

function readBrandingNumber(
  branding: Record<string, unknown> | null | undefined,
  key: string,
  fallback: number,
): number {
  const value = branding?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Per-business defaults: org-level fields (currency, prefix, branding)
 * plus the app-defaults blob (due days, payment instructions).
 */
export function DefaultsScreen() {
  const orgId = useActiveOrganizationId();
  const { organization, loading: loadingOrg } = useOrganization(orgId ?? '');
  const { appDefaults, loading: loadingDefaults, error } = useAppDefaults(orgId ?? '');
  const { updateDefaults, loading: savingDefaults } = useUpdateAppDefaults();
  const { updateOrganization, loading: savingOrg } = useUpdateOrganization();
  const { currencies } = useSupportedCurrencies();
  const { toast } = useToast();
  const { canEditOrg } = useActiveRole();

  const [currency, setCurrency] = useState('USD');
  const [invoicePrefix, setInvoicePrefix] = useState('INV');
  const [numberPadding, setNumberPadding] = useState('4');
  const [defaultTaxName, setDefaultTaxName] = useState('');
  const [defaultTaxRate, setDefaultTaxRate] = useState('');
  const [termsPreset, setTermsPreset] = useState('net14');
  const [defaultNotes, setDefaultNotes] = useState('');
  const [autoReminders, setAutoReminders] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [dueDays, setDueDays] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');

  useEffect(() => {
    if (!organization) return;
    const branding = (organization.branding ?? null) as Record<string, unknown> | null;
    const taxRaw = branding?.defaultTax;
    const taxObj =
      typeof taxRaw === 'object' && taxRaw !== null
        ? (taxRaw as Record<string, unknown>)
        : null;
    setCurrency(organization.currency ?? 'USD');
    setInvoicePrefix(organization.invoicePrefix ?? 'INV');
    setNumberPadding(String(readBrandingNumber(branding, 'invoiceNumberPadding', 4)));
    setDefaultTaxName(typeof taxObj?.name === 'string' ? taxObj.name : '');
    setDefaultTaxRate(
      typeof taxObj?.rate === 'number' && Number.isFinite(taxObj.rate)
        ? String(taxObj.rate)
        : '',
    );
    setTermsPreset(readBrandingString(branding, 'defaultTerms') || 'net14');
    setDefaultNotes(readBrandingString(branding, 'defaultNotes'));
    setAutoReminders(readBrandingBoolean(branding, 'autoReminders', true));
    setShowLogo(readBrandingBoolean(branding, 'showLogo', true));
  }, [organization?.id, organization?.currency, organization?.invoicePrefix, organization?.branding]);

  useEffect(() => {
    if (!appDefaults) return;
    const settings = (appDefaults.settings ?? {}) as Record<string, unknown>;
    setDueDays(typeof settings.dueDays === 'number' ? String(settings.dueDays) : '');
    setPaymentInstructions(
      typeof settings.paymentInstructions === 'string' ? settings.paymentInstructions : '',
    );
  }, [appDefaults]);

  const currencyList = useMemo(() => {
    const codes = (currencies ?? []).map((c) => c.code);
    return codes.length > 0 ? codes : [...FALLBACK_CURRENCIES];
  }, [currencies]);

  const previewSeq = useMemo(() => {
    const padding = Math.max(1, Math.min(10, Number(numberPadding) || 4));
    const next = (organization?.nextInvoiceSeq ?? 1).toString();
    return next.padStart(padding, '0');
  }, [numberPadding, organization?.nextInvoiceSeq]);

  const loading = loadingOrg || loadingDefaults;
  const saving = savingOrg || savingDefaults;

  if (!orgId) {
    return (
      <Page>
        <PageHeader title="Defaults" />
        <Alert variant="danger">No active business selected.</Alert>
      </Page>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!orgId || !organization || !canEditOrg) return;

    const parsedDueDays = dueDays.trim() === '' ? null : Number(dueDays);
    if (
      parsedDueDays !== null &&
      (!Number.isFinite(parsedDueDays) || parsedDueDays < 0 || parsedDueDays > 365)
    ) {
      toast({
        title: 'Invalid due-by',
        description: 'Enter a number of days between 0 and 365.',
        variant: 'danger',
      });
      return;
    }

    const padding = Math.max(1, Math.min(10, Number(numberPadding) || 4));
    const trimmedPrefix = invoicePrefix.trim() || 'INV';
    const trimmedNotes = defaultNotes.trim();
    const trimmedTaxName = defaultTaxName.trim();
    const taxRateNum = Number(defaultTaxRate);

    const branding: Record<string, unknown> = {
      invoiceNumberPadding: padding,
      defaultTerms: termsPreset,
      defaultNotes: trimmedNotes,
      autoReminders,
      showLogo,
    };
    if (trimmedTaxName.length > 0 && Number.isFinite(taxRateNum)) {
      branding.defaultTax = { name: trimmedTaxName, rate: taxRateNum };
    }

    const appUpdates: Record<string, unknown> = {
      dueDays: parsedDueDays,
      defaultNotes: trimmedNotes || null,
      defaultTerms: termsPreset,
      paymentInstructions: paymentInstructions || null,
    };

    try {
      await Promise.all([
        updateOrganization({
          organizationId: orgId,
          updates: {
            currency,
            invoicePrefix: trimmedPrefix,
            branding,
          },
        }),
        updateDefaults({ organizationId: orgId, updates: appUpdates }),
      ]);
      toast({ title: 'Defaults saved', variant: 'success' });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Page>
      <PageHeader
        title="Defaults"
        description="Currency, numbering, tax, and invoice boilerplate for this business."
      />

      {loading ? (
        <Skeleton className="h-[60vh] rounded-xl" />
      ) : error ? (
        <Alert variant="danger">{describeAppError(error).message}</Alert>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-6">
          <fieldset
            disabled={!canEditOrg}
            className="flex flex-col gap-6 disabled:opacity-60"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Invoice numbering</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Field label="Currency" htmlFor="defaultsCurrency">
                  <div className="flex flex-wrap gap-2">
                    {currencyList.map((code) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => setCurrency(code)}
                        className={cn(
                          'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                          currency === code
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-line bg-card text-foreground hover:border-line-strong',
                        )}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Invoice prefix" htmlFor="invoicePrefix">
                    <Input
                      id="invoicePrefix"
                      value={invoicePrefix}
                      onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase())}
                      maxLength={8}
                    />
                  </Field>
                  <Field
                    label="Number padding"
                    htmlFor="numberPadding"
                    hint={`Next invoice: ${invoicePrefix}-${previewSeq}`}
                  >
                    <Input
                      id="numberPadding"
                      type="number"
                      min={1}
                      max={10}
                      value={numberPadding}
                      onChange={(e) => setNumberPadding(e.target.value)}
                    />
                  </Field>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Applied to every invoice</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Field label="Payment terms" htmlFor="termsPreset">
                  <Select value={termsPreset} onValueChange={setTermsPreset}>
                    <SelectTrigger id="termsPreset">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAY_TERMS.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Default tax name" htmlFor="defaultTaxName">
                    <Input
                      id="defaultTaxName"
                      value={defaultTaxName}
                      onChange={(e) => setDefaultTaxName(e.target.value)}
                      placeholder="e.g. Sales tax"
                    />
                  </Field>
                  <Field label="Default tax rate (%)" htmlFor="defaultTaxRate">
                    <Input
                      id="defaultTaxRate"
                      value={defaultTaxRate}
                      onChange={(e) => setDefaultTaxRate(e.target.value)}
                      placeholder="e.g. 7.25"
                    />
                  </Field>
                </div>

                <Field label="Default notes" htmlFor="defaultNotes">
                  <Textarea
                    id="defaultNotes"
                    rows={3}
                    value={defaultNotes}
                    onChange={(e) => setDefaultNotes(e.target.value)}
                  />
                </Field>

                <Field
                  label="Due in (days)"
                  htmlFor="dueDays"
                  hint="Overrides the preset when computing the default due date on new invoices."
                >
                  <Input
                    id="dueDays"
                    type="number"
                    min={0}
                    max={365}
                    value={dueDays}
                    onChange={(e) => setDueDays(e.target.value)}
                    placeholder="e.g. 14"
                  />
                </Field>

                <Field label="Payment instructions" htmlFor="paymentInstructions">
                  <Textarea
                    id="paymentInstructions"
                    rows={3}
                    value={paymentInstructions}
                    onChange={(e) => setPaymentInstructions(e.target.value)}
                  />
                </Field>

                <div className="flex flex-col gap-3">
                  <ToggleRow
                    id="autoReminders"
                    label="Automatic reminders"
                    description="Send polite nudges when invoices go overdue."
                    checked={autoReminders}
                    onChange={setAutoReminders}
                  />
                  <ToggleRow
                    id="showLogo"
                    label="Show logo on invoice"
                    description="Include your business logo on PDFs and the public view."
                    checked={showLogo}
                    onChange={setShowLogo}
                  />
                </div>
              </CardContent>
            </Card>

            {canEditOrg ? (
              <div className="flex justify-end">
                <Button type="submit" loading={saving}>
                  <Save className="size-4" /> Save defaults
                </Button>
              </div>
            ) : null}
          </fieldset>
        </form>
      )}
    </Page>
  );
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-line p-4">
      <div>
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 size-4 rounded border-line"
      />
    </div>
  );
}
